import { computed } from "@vue/composition-api";

/**
 * Shared helper for the panel config tabs so they can edit data in
 * `source === 'template'` mode.
 *
 * In template mode there is no live panel to POST to: edits must stay in the
 * exact GET shape (the one returned by `panel-config` / the template JSON) so
 * that `Index.vue#saveTemplate` can serialize the whole template untouched.
 * Components keep their normal POST path for the real-panel modes and only add
 * a `isTemplateMode` early-return branch that calls `emitTemplateUpdate`.
 *
 * @param {object} props - the component props (must declare a `source` prop)
 * @param {(event: string, payload: any) => void} emit - the component emit fn
 */
export function useTemplateMode(props, emit) {
  const isTemplateMode = computed(() => props.source === "template");

  /**
   * Send a section back to the parent in GET shape. The parent merges it into
   * `panel.value[section]`; nothing is persisted until "Save Template".
   *
   * @param {string} section - top-level key as it comes from the GET response
   *                            (e.g. "users", "communication_paths", "areas")
   * @param {*} data - the section value already in GET shape
   */
  const emitTemplateUpdate = (section, data) => {
    emit("template-update", { [section]: data });
  };

  /**
   * En modo template no hay POST que devuelva el id autogenerado del registro.
   * Devuelve el siguiente id en secuencia (max(id existentes) + 1, o 1 si la
   * lista está vacía), replicando el auto-increment del backend (no reutiliza
   * ids de items borrados).
   *
   * @param {Array} list - colección actual de la sección (forma GET)
   * @returns {number}
   */
  const nextSequentialId = (list) => {
    const ids = (Array.isArray(list) ? list : [])
      .map((x) => parseInt(x && x.id, 10))
      .filter((n) => !isNaN(n));
    return ids.length ? Math.max(...ids) + 1 : 1;
  };

  return { isTemplateMode, emitTemplateUpdate, nextSequentialId };
}
